<template>
  <div class="page-wrapper">
    <HeaderComponent />
    <div class="page-content">
      <AppBreadcrumb :items="[{ label: 'Musobaqa' }]" />

      <!-- LOBBY: Yaratish / Qo'shilish -->
      <template v-if="!roomCode || phase === 'menu'">
        <div class="challenge-menu">
          <div class="menu-card create-card" @click="showCreateModal = true">
            <div class="menu-icon-box create-bg"><TrophyOutlined class="menu-svg-icon" /></div>
            <h3>Xona yaratish</h3>
            <p>Yangi musobaqa xonasini yarating va do'stlaringizni taklif qiling</p>
          </div>
          <div class="menu-card join-card" @click="showJoinModal = true">
            <div class="menu-icon-box join-bg"><TeamOutlined class="menu-svg-icon" /></div>
            <h3>Xonaga qo'shilish</h3>
            <p>6 xonali kod orqali mavjud musabaqaga qo'shiling</p>
          </div>
        </div>
      </template>

      <!-- WAITING ROOM -->
      <template v-else-if="phase === 'waiting'">
        <div class="waiting-room">
          <div class="room-header-card">
            <div class="room-code-section">
              <span class="room-code-label">Xona kodi:</span>
              <span class="room-code-value" @click="copyCode">{{ roomCode }}</span>
              <a-button size="small" @click="copyCode"><CopyOutlined /></a-button>
            </div>
            <div class="room-info">
              <span>Savollar: <b>{{ roomData?.question_count || 20 }}</b></span>
              <span>Ishtirokchilar: <b>{{ participants.length }}</b></span>
            </div>
          </div>

          <div class="participants-list">
            <h3><TeamOutlined /> Ishtirokchilar</h3>
            <div v-for="p in participants" :key="p.id" class="participant-item">
              <div class="participant-avatar">{{ getInitials(p.name) }}</div>
              <span class="participant-name">{{ p.name }}</span>
              <a-tag v-if="p.is_creator" color="blue">Admin</a-tag>
              <a-tag v-if="p.id === currentUserId" color="green">Siz</a-tag>
            </div>
          </div>

          <div class="waiting-actions">
            <a-button v-if="isCreator" type="primary" size="large" block :loading="starting"
              :disabled="participants.length < 2" @click="startChallenge">
              <template #icon><ThunderboltOutlined /></template>
              Musabaqani boshlash
            </a-button>
            <p v-if="isCreator && participants.length < 2" class="hint-text">
              Kamida 2 ta ishtirokchi kerak
            </p>
            <p v-if="!isCreator" class="hint-text">
              <LoadingOutlined spin /> Admin musabaqani boshlashini kutilmoqda...
            </p>
          </div>
        </div>
      </template>

      <!-- TEST PHASE -->
      <template v-else-if="phase === 'testing'">
        <div class="test-phase">
          <div class="test-top-bar">
            <span>Savol {{ currentIndex + 1 }} / {{ questions.length }}</span>
            <div class="top-bar-right">
              <a-tag color="green" class="answered-tag">
                ✅ {{ answeredCount }} / {{ questions.length }}
              </a-tag>
              <span class="timer"><ClockCircleOutlined /> {{ formattedTime }}</span>
              <a-button type="primary" danger size="small" @click="finishChallenge" :loading="finishing" :disabled="finishing">
                Yakunlash
              </a-button>
            </div>
          </div>

          <div v-if="currentQuestion" class="test-container-ch">
            <!-- CHAP PANEL: Savol + variantlar -->
            <div class="question-panel-ch">
              <div class="panel-card-ch">
                <div class="title-row-ch">
                  <div>
                    <div class="test-title-ch">Musobaqa</div>
                    <div class="test-subtitle-ch">Savol {{ currentIndex + 1 }} / {{ questions.length }}</div>
                  </div>
                  <div class="mode-badge-ch">
                    <span class="mode-badge-icon-ch">🏆</span>
                    <span class="mode-badge-text-ch">Musobaqa</span>
                  </div>
                </div>

                <div class="question-content-ch">
                  <h2 class="question-title-ch">{{ currentQuestion.text }}</h2>

                  <div class="options-ch">
                    <button
                      v-for="(opt, idx) in currentQuestion.options"
                      :key="opt.id"
                      class="option-btn-ch"
                      :class="getOptionClass(opt)"
                      :disabled="finishing || isQuestionAnswered(currentQuestion.id)"
                      @click="selectAnswer(currentQuestion.id, opt.id)"
                    >
                      <span class="option-hotkey-ch">F{{ idx + 1 }}</span>
                      <span class="option-text-ch">{{ opt.text }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- O'NG PANEL: Rasm + Izoh -->
            <div class="info-panel-ch">
              <div class="panel-card-ch">
                <div class="image-wrap-ch">
                  <a-image
                    v-if="currentQuestion.image_url"
                    :src="currentQuestion.image_url"
                    class="question-image-ch"
                    alt="Savol rasmi"
                  />
                  <div v-else class="no-image-ch">
                    <img src="../../public/no-image.png" alt="Rasm yo'q" />
                  </div>
                </div>

                <div v-if="showExplanation" class="explanation-ch">
                  <div class="explanation-title-ch">
                    <span class="explanation-icon-ch">💡</span>
                    Izoh
                  </div>
                  <div class="explanation-text-ch">
                    {{ currentQuestion.explanation || 'Izoh mavjud emas' }}
                  </div>

                  <div v-if="isCurrentAnswerCorrect" class="result-alert-ch success-ch">
                    <span class="alert-icon-ch">✅</span>
                    <span class="alert-text-ch">To'g'ri javob!</span>
                  </div>

                  <div v-else-if="isCurrentAnswerWrong" class="result-alert-ch error-ch">
                    <span class="alert-icon-ch">❌</span>
                    <span class="alert-text-ch">Noto'g'ri javob!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pastki navigatsiya -->
          <div class="bottom-nav-ch">
            <div class="question-number-grid-ch">
              <button
                v-for="(q, i) in questions"
                :key="q.id"
                class="question-nav-btn-ch"
                :class="getQuestionBtnClass(i)"
                @click="currentIndex = i; restoreExplanationState()"
              >{{ i + 1 }}</button>
            </div>
          </div>
        </div>
      </template>

      <!-- RESULTS -->
      <template v-else-if="phase === 'results'">
        <div class="results-section">
          <div class="results-header">
            <TrophyOutlined class="results-trophy" />
            <h2>Natijalar</h2>
          </div>

          <div class="leaderboard">
            <div v-for="entry in leaderboard" :key="entry.user_id"
              class="lb-row" :class="{ 'is-me': entry.is_me, 'top-3': entry.rank <= 3 }">
              <div class="lb-rank" :class="`rank-${entry.rank}`">
                <span v-if="entry.rank === 1">🥇</span>
                <span v-else-if="entry.rank === 2">🥈</span>
                <span v-else-if="entry.rank === 3">🥉</span>
                <span v-else>{{ entry.rank }}</span>
              </div>
              <div class="lb-info">
                <span class="lb-name">{{ entry.name }}</span>
                <span v-if="entry.is_me" class="lb-me-tag">(Siz)</span>
              </div>
              <div class="lb-stats">
                <span class="lb-score">{{ entry.score_percent }}%</span>
                <span class="lb-detail">{{ entry.correct_count }}/{{ entry.correct_count + entry.wrong_count + entry.unanswered_count }}</span>
              </div>
            </div>
          </div>

          <a-button type="primary" block size="large" @click="resetAll" style="margin-top:20px">
            Bosh sahifaga qaytish
          </a-button>
        </div>
      </template>

      <!-- Create Modal -->
      <a-modal v-model:visible="showCreateModal" title="Xona yaratish" :footer="null" centered>
        <p>Yangi musobaqa xonasi yaratiladi. 20 ta random savol tanlangan bo'ladi.</p>
        <a-button type="primary" block :loading="creating" @click="createRoom">
          <template #icon><PlusOutlined /></template> Yaratish
        </a-button>
      </a-modal>

      <!-- Join Modal -->
      <a-modal v-model:visible="showJoinModal" title="Xonaga qo'shilish" :footer="null" centered>
        <a-input v-model:value="joinCode" placeholder="6 xonali kodni kiriting" maxlength="6" size="large"
          style="text-align:center; font-size:24px; letter-spacing:8px; font-weight:700" />
        <a-button type="primary" block :loading="joining" :disabled="joinCode.length !== 6"
          @click="joinRoom" style="margin-top:16px">
          <template #icon><LoginOutlined /></template> Qo'shilish
        </a-button>
      </a-modal>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import { message } from 'ant-design-vue'
import HeaderComponent from '@/components/HeaderComponent.vue'
import AppBreadcrumb from '@/components/AppBreadcrumb.vue'
import {
  TrophyOutlined, TeamOutlined, CopyOutlined, ThunderboltOutlined,
  LoadingOutlined, ClockCircleOutlined, PlusOutlined, LoginOutlined
} from '@ant-design/icons-vue'

export default {
  name: 'ChallengeView',
  components: {
    HeaderComponent, AppBreadcrumb, TrophyOutlined, TeamOutlined,
    CopyOutlined, ThunderboltOutlined, LoadingOutlined, ClockCircleOutlined,
    PlusOutlined, LoginOutlined
  },
  data() {
    return {
      phase: 'menu', // menu | waiting | testing | results
      roomCode: null, roomData: null, participants: [],
      showCreateModal: false, showJoinModal: false,
      joinCode: '', creating: false, joining: false, starting: false, finishing: false,
      questions: [], currentIndex: 0, selectedAnswers: {},
      showExplanation: false,
      timerSeconds: 0, timerInterval: null,
      leaderboard: [], ws: null,
    }
  },
  computed: {
    currentUserId() { return JSON.parse(localStorage.getItem('user') || '{}').id },
    isCreator() { return this.roomData?.creator?.id === this.currentUserId },
    currentQuestion() { return this.questions[this.currentIndex] || null },
    formattedTime() {
      const m = Math.floor(this.timerSeconds / 60)
      const s = this.timerSeconds % 60
      return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
    },
    answeredCount() {
      return Object.keys(this.selectedAnswers).length
    },
    isCurrentAnswerCorrect() {
      if (!this.currentQuestion?.id) return false
      const selectedId = this.selectedAnswers[this.currentQuestion.id]
      const correctId = this.getCorrectOptionId(this.currentQuestion)
      if (!selectedId || !correctId) return false
      return Number(selectedId) === Number(correctId)
    },
    isCurrentAnswerWrong() {
      if (!this.currentQuestion?.id) return false
      const selectedId = this.selectedAnswers[this.currentQuestion.id]
      const correctId = this.getCorrectOptionId(this.currentQuestion)
      if (!selectedId || !correctId) return false
      return Number(selectedId) !== Number(correctId)
    }
  },
  beforeUnmount() {
    this.disconnectWS()
    clearInterval(this.timerInterval)
  },
  methods: {
    // ============ WEBSOCKET ============
    getWSUrl(roomCode) {
      const token = localStorage.getItem('accessToken') ||
        localStorage.getItem('access') ||
        localStorage.getItem('token') ||
        localStorage.getItem('access_token')
      const apiUrl = process.env.VUE_APP_API_URL || 'https://avtotesting.uz/api'
      const wsBase = apiUrl.replace('/api', '').replace('http://', 'ws://').replace('https://', 'wss://')
      return `${wsBase}/ws/challenge/${roomCode}/?token=${token}`
    },
    connectWS(roomCode) {
      this.disconnectWS()
      this._tryConnectWS(roomCode)
    },
    _tryConnectWS(roomCode) {
      const url = this.getWSUrl(roomCode)
      try {
        this.ws = new WebSocket(url)
      } catch {
        setTimeout(() => {
          if (this.roomCode && this.phase !== 'menu') {
            this._tryConnectWS(this.roomCode)
          }
        }, 3000)
        return
      }

      this.ws.onopen = () => {
        console.log('🔗 WebSocket ulandi:', roomCode)
      }

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          this.handleWSMessage(msg)
        } catch (e) {
          // Silently ignore parse errors
        }
      }

      this.ws.onclose = (e) => {
        if (this.roomCode && this.phase !== 'menu' && e.code !== 1000) {
          setTimeout(() => {
            if (this.roomCode && this.phase !== 'menu') {
              this._tryConnectWS(this.roomCode)
            }
          }, 3000)
        }
      }

      this.ws.onerror = () => {
        // Silently handle — onclose will trigger retry
      }
    },
    disconnectWS() {
      if (this.ws) {
        this.ws.onclose = null
        this.ws.onerror = null
        this.ws.close()
        this.ws = null
      }
    },
    handleWSMessage(msg) {
      const { type, data } = msg

      switch (type) {
        case 'room_update':
        case 'participant_joined':
          this.roomData = data
          this.participants = data.participants || []
          break

        case 'challenge_started':
          this.roomData = data
          this.participants = data.participants || []
          if (this.phase === 'waiting') {
            this.loadQuestions()
          }
          break

        case 'challenge_finished':
          this.roomData = data
          // Barcha yakunladi — natijalarni yangilash
          this.loadResults()
          break

        case 'participant_finished':
          this.roomData = data
          this.participants = data.participants || []
          // Natijalar sahifasida bo'lsa, leaderboard'ni yangilash
          if (this.phase === 'results') {
            this.loadResults()
          }
          break

        default:
          console.log('Noma\'lum WS event:', type, data)
      }
    },

    // ============ ROOM ACTIONS ============
    getInitials(name) { return (name || '?').substring(0, 2).toUpperCase() },
    async copyCode() {
      try { await navigator.clipboard.writeText(this.roomCode); message.success('Kod nusxalandi!') }
      catch { message.info(this.roomCode) }
    },
    async createRoom() {
      this.creating = true
      try {
        const { data } = await api.post('/v1/challenge/create/', { question_count: 20 })
        this.roomCode = data.code; this.roomData = data
        this.participants = [{ id: this.currentUserId, name: data.creator.name, is_creator: true }]
        this.phase = 'waiting'; this.showCreateModal = false
        this.connectWS(data.code)
        message.success(`Xona yaratildi! Kod: ${data.code}`)
      } catch (e) { message.error(e.response?.data?.detail || 'Xatolik') }
      finally { this.creating = false }
    },
    async joinRoom() {
      this.joining = true
      try {
        const { data } = await api.post('/v1/challenge/join/', { code: this.joinCode })
        this.roomCode = data.code; this.roomData = data
        this.participants = data.participants || []
        this.phase = 'waiting'; this.showJoinModal = false
        this.connectWS(data.code)
        message.success('Xonaga qo\'shildingiz!')
      } catch (e) { message.error(e.response?.data?.detail || 'Xona topilmadi') }
      finally { this.joining = false }
    },
    async startChallenge() {
      this.starting = true
      try {
        await api.post(`/v1/challenge/start/${this.roomCode}/`)
        this.loadQuestions()
      } catch (e) { message.error(e.response?.data?.detail || 'Xatolik') }
      finally { this.starting = false }
    },
    async loadQuestions() {
      try {
        const { data } = await api.get(`/v1/challenge/questions/${this.roomCode}/`)
        this.questions = data.questions || []; this.currentIndex = 0
        this.selectedAnswers = {}; this.phase = 'testing'
        this.showExplanation = false
        this.timerSeconds = 0
        clearInterval(this.timerInterval)
        this.timerInterval = setInterval(() => { this.timerSeconds++ }, 1000)
      } catch (e) { message.error(e.response?.data?.detail || 'Savollarni yuklashda xato') }
    },

    // ============ TEST HELPERS ============
    isQuestionAnswered(questionId) {
      return !!this.selectedAnswers[questionId]
    },
    getCorrectOptionId(question) {
      if (!question?.options?.length) return null
      const correctOpt = question.options.find(o => o.is_correct === true)
      return correctOpt ? correctOpt.id : null
    },
    getOptionClass(option) {
      const classes = []
      const selectedId = this.selectedAnswers[this.currentQuestion?.id]
      const correctId = this.getCorrectOptionId(this.currentQuestion)
      if (!selectedId) return classes
      if (Number(option.id) === Number(selectedId)) {
        classes.push('is-selected')
        if (Number(selectedId) === Number(correctId)) {
          classes.push('is-correct')
        } else {
          classes.push('is-wrong')
        }
      }
      if (correctId && Number(selectedId) !== Number(correctId) && Number(option.id) === Number(correctId)) {
        classes.push('is-correct')
      }
      return classes
    },
    getQuestionBtnClass(index) {
      const classes = []
      const question = this.questions[index]
      if (this.currentIndex === index) classes.push('active')
      if (question) {
        const selectedId = this.selectedAnswers[question.id]
        if (selectedId) {
          const correctId = this.getCorrectOptionId(question)
          if (Number(selectedId) === Number(correctId)) {
            classes.push('answered-correct')
          } else {
            classes.push('answered-wrong')
          }
        }
      }
      return classes
    },
    restoreExplanationState() {
      this.showExplanation = !!this.selectedAnswers[this.currentQuestion?.id]
    },
    selectAnswer(qId, optId) {
      if (this.isQuestionAnswered(qId)) return
      this.selectedAnswers = { ...this.selectedAnswers, [qId]: optId }
      this.showExplanation = true

      // Avtomatik keyingi savolga o'tish (agar to'g'ri bo'lsa)
      setTimeout(() => {
        if (this.answeredCount === this.questions.length) return
        const correctId = this.getCorrectOptionId(this.currentQuestion)
        if (Number(optId) === Number(correctId) && this.currentIndex < this.questions.length - 1) {
          this.currentIndex++
          this.restoreExplanationState()
        }
      }, 400)
    },

    // ============ FINISH & RESULTS ============
    async finishChallenge() {
      this.finishing = true
      clearInterval(this.timerInterval)
      const answers = this.questions.map(q => ({
        question_id: q.id,
        selected_option_id: this.selectedAnswers[q.id] || null
      }))
      try {
        await api.post(`/v1/challenge/finish/${this.roomCode}/`, {
          answers, duration_seconds: this.timerSeconds
        })
        message.success('Javoblar yuborildi!')
        this.loadResults()
      } catch (e) { message.error(e.response?.data?.detail || 'Xatolik') }
      finally { this.finishing = false }
    },
    async loadResults() {
      try {
        const { data } = await api.get(`/v1/challenge/results/${this.roomCode}/`)
        this.leaderboard = data.leaderboard || []; this.phase = 'results'
        // WebSocket orqali yangilanishlar keladi, polling kerak emas
      } catch {}
    },
    resetAll() {
      this.disconnectWS()
      clearInterval(this.timerInterval)
      this.phase = 'menu'; this.roomCode = null; this.roomData = null
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
/* Menu */
.challenge-menu { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-top: 20px; }
.menu-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: 20px; padding: 32px 24px; text-align: center; cursor: pointer; transition: all 0.3s; }
.menu-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.12); }
.menu-icon-box { width: 64px; height: 64px; border-radius: 18px; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
.menu-svg-icon { font-size: 28px; color: #fff; }
.create-bg { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.join-bg { background: linear-gradient(135deg, #10b981, #059669); }
.menu-card h3 { font-size: 18px; font-weight: 700; color: var(--text); margin: 0 0 8px; }
.menu-card p { font-size: 13px; color: var(--text-soft); margin: 0; }

/* Waiting Room */
.waiting-room { max-width: 600px; margin: 20px auto; }
.room-header-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: 16px; padding: 20px; margin-bottom: 20px; }
.room-code-section { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.room-code-label { font-size: 14px; color: var(--text-soft); }
.room-code-value { font-size: 32px; font-weight: 800; letter-spacing: 6px; color: var(--primary); cursor: pointer; }
.room-info { display: flex; gap: 20px; font-size: 13px; color: var(--text-soft); }
.participants-list { background: var(--bg-card); border: 1px solid var(--border); border-radius: 16px; padding: 20px; margin-bottom: 20px; }
.participants-list h3 { font-size: 16px; margin: 0 0 16px; color: var(--text); }
.participant-item { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--border); }
.participant-item:last-child { border-bottom: none; }
.participant-avatar { width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, var(--primary), #6366f1); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; }
.participant-name { flex: 1; font-size: 14px; color: var(--text); font-weight: 500; }
.waiting-actions { text-align: center; }
.hint-text { font-size: 13px; color: var(--text-soft); margin-top: 12px; }

/* Test Phase */
.test-phase { max-width: 1400px; margin: 0 auto; padding-bottom: 100px; }
.test-top-bar { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: var(--bg-card); border: 1px solid var(--border); border-radius: 12px; margin-bottom: 16px; font-weight: 600; color: var(--text); }
.top-bar-right { display: flex; align-items: center; gap: 12px; }
.answered-tag { font-weight: 600; }
.timer { color: var(--danger); font-weight: 600; }

/* Two-column test layout */
.test-container-ch { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.question-panel-ch, .info-panel-ch { min-width: 0; }

.panel-card-ch {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 24px;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.title-row-ch { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.test-title-ch { font-size: 18px; font-weight: 800; color: var(--text); }
.test-subtitle-ch { font-size: 13px; color: var(--text-soft); margin-top: 4px; }

.mode-badge-ch {
  background: rgba(245, 158, 11, 0.15);
  padding: 6px 14px;
  border-radius: 30px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(245, 158, 11, 0.3);
}
.mode-badge-icon-ch { font-size: 14px; }
.mode-badge-text-ch { font-size: 12px; font-weight: 600; color: #f59e0b; }

.question-content-ch { flex: 1; }
.question-title-ch { font-size: 22px; line-height: 1.4; margin: 0 0 24px; color: var(--text); font-weight: 700; word-wrap: break-word; }

/* Options */
.options-ch { display: flex; flex-direction: column; gap: 12px; }
.option-btn-ch {
  min-height: 56px;
  height: auto;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  background: var(--bg-card);
  border: 2px solid var(--border);
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  font-size: 14px;
  color: var(--text);
}
.option-btn-ch:hover:not(:disabled) { transform: translateX(4px); border-color: var(--primary); }
.option-btn-ch:disabled { cursor: default; }
.option-btn-ch.is-selected { border-width: 2px; }
.option-btn-ch.is-correct {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}
.option-btn-ch.is-wrong {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.option-hotkey-ch {
  min-width: 36px;
  height: 28px;
  border-radius: 8px;
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-soft);
  border: 1px solid var(--border);
  flex-shrink: 0;
}
.option-text-ch { flex: 1; line-height: 1.5; }

/* Image panel */
.image-wrap-ch { margin-bottom: 16px; }
.question-image-ch {
  width: 100%;
  border-radius: 16px;
  object-fit: contain;
  max-height: 400px;
  border: 1px solid var(--border);
}
.no-image-ch {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: var(--bg);
  border-radius: 16px;
  border: 1px dashed var(--border);
}
.no-image-ch img {
  max-width: 120px;
  opacity: 0.4;
}

/* Explanation */
.explanation-ch {
  background: var(--bg);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid var(--border);
  animation: fadeInUp 0.3s ease;
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.explanation-title-ch {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 10px;
}
.explanation-icon-ch { font-size: 18px; }
.explanation-text-ch {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-soft);
}

.result-alert-ch {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  margin-top: 14px;
  font-weight: 600;
  font-size: 14px;
}
.result-alert-ch.success-ch {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #16a34a;
}
.result-alert-ch.error-ch {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #dc2626;
}
.alert-icon-ch { font-size: 18px; }
.alert-text-ch { flex: 1; }

/* Bottom navigation */
.bottom-nav-ch {
  max-width: 1400px;
  margin: 20px auto 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 16px 20px;
}
.question-number-grid-ch {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}
.question-nav-btn-ch {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 2px solid var(--border);
  background: var(--bg-card);
  color: var(--text);
  transition: all 0.15s ease;
}
.question-nav-btn-ch:hover { border-color: var(--primary); }
.question-nav-btn-ch.active {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
  transform: scale(1.1);
}
.question-nav-btn-ch.answered-correct {
  background: rgba(34, 197, 94, 0.15);
  border-color: rgba(34, 197, 94, 0.5);
  color: #22c55e;
}
.question-nav-btn-ch.answered-wrong {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.5);
  color: #ef4444;
}

/* Results */
.results-section { max-width: 600px; margin: 20px auto; }
.results-header { text-align: center; margin-bottom: 24px; }
.results-trophy { font-size: 48px; color: #f59e0b; }
.results-header h2 { font-size: 24px; font-weight: 700; color: var(--text); margin: 8px 0 0; }
.leaderboard { background: var(--bg-card); border: 1px solid var(--border); border-radius: 16px; overflow: hidden; }
.lb-row { display: flex; align-items: center; gap: 14px; padding: 14px 18px; border-bottom: 1px solid var(--border); transition: 0.2s; }
.lb-row:last-child { border-bottom: none; }
.lb-row.is-me { background: rgba(59,130,246,0.06); }
.lb-row.top-3 { font-weight: 600; }
.lb-rank { width: 36px; text-align: center; font-size: 18px; }
.lb-info { flex: 1; }
.lb-name { font-size: 14px; color: var(--text); }
.lb-me-tag { font-size: 11px; color: var(--primary); margin-left: 6px; }
.lb-stats { text-align: right; }
.lb-score { font-size: 18px; font-weight: 700; color: var(--success); display: block; }
.lb-detail { font-size: 11px; color: var(--text-soft); }

/* ===== Tablet & Mobile ===== */
@media (max-width: 768px) {
  .challenge-menu { grid-template-columns: 1fr; gap: 16px; margin-top: 10px; }
  .menu-card { padding: 20px 16px; border-radius: 16px; }
  .menu-icon-box { width: 48px; height: 48px; border-radius: 14px; margin-bottom: 12px; }
  .menu-svg-icon { font-size: 24px; }
  .menu-card h3 { font-size: 16px; margin-bottom: 6px; }
  .menu-card p { font-size: 12px; }

  .waiting-room { margin: 10px auto; width: 100%; }
  .room-header-card { padding: 16px; border-radius: 14px; margin-bottom: 16px; }
  .room-code-section { flex-wrap: wrap; gap: 10px; margin-bottom: 12px; }
  .room-code-value { font-size: 26px; letter-spacing: 4px; }
  .room-info { gap: 16px; font-size: 13px; flex-wrap: wrap; }
  
  .participants-list { padding: 16px; border-radius: 14px; margin-bottom: 16px; }
  .participants-list h3 { font-size: 15px; margin-bottom: 12px; }
  .participant-item { padding: 8px 0; gap: 10px; }
  .participant-avatar { width: 32px; height: 32px; font-size: 11px; }
  .participant-name { font-size: 14px; }

  .test-phase { padding-bottom: 100px; width: 100%; }
  .test-container-ch { grid-template-columns: 1fr; gap: 16px; }
  
  .test-top-bar { 
    flex-direction: row; 
    flex-wrap: wrap;
    gap: 10px; 
    align-items: center; 
    padding: 12px 14px; 
    border-radius: 12px; 
    margin-bottom: 16px; 
    font-size: 13px; 
  }
  .top-bar-right { width: 100%; justify-content: space-between; }
  .answered-tag { font-size: 12px; padding: 2px 8px; }
  .timer { font-size: 13px; }

  .panel-card-ch { padding: 16px; border-radius: 16px; width: 100%; box-sizing: border-box; }
  .title-row-ch { margin-bottom: 16px; }
  .test-title-ch { font-size: 16px; }
  .test-subtitle-ch { font-size: 12px; }
  .question-title-ch { font-size: 16px; margin-bottom: 16px; line-height: 1.4; }

  .option-btn-ch { min-height: 48px; padding: 10px 14px; border-radius: 12px; font-size: 13px; gap: 10px; }
  .option-hotkey-ch { min-width: 30px; height: 26px; font-size: 10px; }

  .image-wrap-ch { margin-bottom: 16px; }
  .question-image-ch { max-height: 240px; border-radius: 12px; }
  .no-image-ch { min-height: 120px; border-radius: 12px; }
  .no-image-ch img { max-width: 60px; }

  .explanation-ch { padding: 16px; border-radius: 12px; }
  .explanation-title-ch { font-size: 14px; margin-bottom: 8px; }
  .explanation-text-ch { font-size: 13px; }
  .result-alert-ch { padding: 10px 14px; font-size: 13px; border-radius: 10px; margin-top: 10px; }

  .bottom-nav-ch {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    border-radius: 0;
    padding: 12px 16px;
    z-index: 100;
    border-top: 1px solid var(--border);
    box-shadow: 0 -4px 16px rgba(0,0,0,0.1);
  }
  .question-number-grid-ch { 
    display: grid; 
    grid-template-columns: repeat(10, 1fr); 
    gap: 6px; 
    justify-content: center;
    max-width: 100%;
  }
  .question-nav-btn-ch { width: 100%; height: 32px; font-size: 12px; border-radius: 8px; border-width: 1px; }

  .results-section { margin: 10px auto; width: 100%; }
  .results-trophy { font-size: 40px; }
  .results-header h2 { font-size: 20px; margin-top: 10px; }
  .lb-row { padding: 12px; gap: 10px; }
  .lb-rank { width: 24px; font-size: 15px; }
  .lb-name { font-size: 13px; }
  .lb-score { font-size: 15px; }
  .lb-detail { font-size: 11px; }
  .leaderboard { border-radius: 12px; }

  .mode-badge-ch { padding: 4px 10px; }
  .mode-badge-icon-ch { font-size: 12px; }
  .mode-badge-text-ch { font-size: 11px; }
}

/* ===== Small Mobile ===== */
@media (max-width: 480px) {
  .menu-card { padding: 16px 12px; }
  .menu-icon-box { width: 40px; height: 40px; border-radius: 10px; margin-bottom: 8px; }
  .menu-svg-icon { font-size: 20px; }
  .menu-card h3 { font-size: 15px; }

  .room-code-value { font-size: 22px; letter-spacing: 3px; }
  .room-code-label { font-size: 12px; }

  .test-top-bar { padding: 10px; font-size: 12px; }
  .panel-card-ch { padding: 14px; }
  .test-title-ch { font-size: 15px; }
  .question-title-ch { font-size: 15px; margin-bottom: 12px; }
  .option-btn-ch { min-height: 44px; padding: 8px 12px; font-size: 12px; gap: 8px; }
  .option-hotkey-ch { min-width: 26px; height: 24px; font-size: 9px; border-radius: 6px; }

  .question-image-ch { max-height: 180px; }
  .no-image-ch { min-height: 100px; }

  .explanation-ch { padding: 12px; }
  .explanation-title-ch { font-size: 13px; gap: 6px; }
  .explanation-text-ch { font-size: 12px; }

  .bottom-nav-ch { padding: 10px; }
  .question-number-grid-ch { 
    grid-template-columns: repeat(10, 1fr); 
    gap: 4px; 
  }
  .question-nav-btn-ch { height: 28px; font-size: 11px; }

  .results-header h2 { font-size: 18px; }
  .lb-row { padding: 10px; gap: 8px; }
  .lb-name { font-size: 12px; }
  .lb-score { font-size: 14px; }
  .lb-me-tag { font-size: 10px; }
}
</style>
